/* eslint-disable indent */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  Conditional,
  Space,
  Text,
  Button,
  Popup,
  Progress,
} from '@cashfree-intl/coherent';

import { CURRENCY_SYMBOL, PLACEHOLDER_TEXT } from 'utils/constants';
import {
  roundOffNumber,
  numberWithCommas,
  convertAmountToAbbreviation,
} from 'utils/common';

import CustomIcon from 'components/CustomIcons';
import InlineLoader from 'components/InlineLoader';
import { ON_DEMAND_LIMIT } from '../constants';

import EVENTS from '../../../constants/analytics';
import Analytics from '../../../utils/analytics';

import freetrial from '../../../images/free-trial-tag.svg';
import timerEllipse from '../../../images/timer-ellipse.svg';

export const getRenewsInText = currTime => {
  const hours = 23 - currTime.hours();
  const mins = 60 - currTime.minute();

  if (mins === 60) {
    return {
      hours: hours + 1,
      mins: 0,
    };
  }
  return { hours, mins };
};

export const handleInfoHover = type => () => {
  Analytics.track(EVENTS.SETTLEMENTS.INFO_ICON, { type });
};

const OnDemandSummaryCard = ({
  isFreeTrialActive,
  freeTrialObj,
  eligibleFundsData,
  refreshCardData,
  onDemandDailyLimit,
  remainingSettlementsLimit,
  onDemandLimitLoader,
  onWithdrawNow,
  disableSettleNow,
  disabledInfoText,
}) => {
  const todayLimit = isFreeTrialActive
    ? freeTrialObj.totalAmount
    : onDemandDailyLimit;
  const remainingLimit =
    (isFreeTrialActive
      ? freeTrialObj.balanceAmount
      : remainingSettlementsLimit) || 0;

  const showProgressBar =
    !Number.isNaN(remainingLimit) && !Number.isNaN(todayLimit);

  const percent =
    100 - (todayLimit ? ((todayLimit - remainingLimit) / todayLimit) * 100 : 0);

  const { hours, mins } = getRenewsInText(moment());

  const withdrawNowButton = (
    <Button
      className="withdraw-now-cta"
      primary
      disabled={disableSettleNow || onDemandLimitLoader}
      type="button"
      onClick={onWithdrawNow}
      data-event-name="Primary_Button_WithdrawNow"
    >
      Withdraw Now
    </Button>
  );

  return (
    <div
      className={`on-demand-card ${
        isFreeTrialActive ? 'position_relative' : ''
      }`}
    >
      <Conditional if={isFreeTrialActive}>
        <img src={freetrial} alt="Free Trial Mode" className="free-trial-tag" />
        <Space alignItems="center" className="free-trial-meta-data mb-3">
          <img
            src={timerEllipse}
            alt="timer-ellipse"
            className="timer-ellipse"
          />
          {/* Add dynamicity here */}
          <Text variant="b12" color="warning">
            {freeTrialObj && freeTrialObj.daysLeftToExpiry
              ? `${freeTrialObj.daysLeftToExpiry} days left!`
              : 'Expiring today'}
          </Text>
        </Space>
      </Conditional>
      <Space alignItems="start" justifyContent="space-between">
        <div>
          <Space alignItems="center" gap={1} className="mb-1">
            <Text variant="b14" color="bodyLight">
              Withdrawal Limit
            </Text>
          </Space>
          <InlineLoader
            loading={onDemandLimitLoader}
            content={
              <Text variant="h28" className="withdrawable-limit">
                {CURRENCY_SYMBOL.INR}{' '}
                {eligibleFundsData.eligibleFunds
                  ? numberWithCommas(
                      roundOffNumber(eligibleFundsData.eligibleFunds || 0),
                    ) // eslint-disable-line
                  : '0.00'}
              </Text>
            }
          />
        </div>
        <Conditional if={showProgressBar}>
          <div>
            <Space alignItems="center" className="mb-1">
              <Text
                variant="b12"
                className="w-400 font-normal"
                color="bodyLight"
              >
                <Conditional if={isFreeTrialActive}>
                  Free Trial Usage:{' '}
                </Conditional>
                <Conditional if={!isFreeTrialActive}>Daily Limit: </Conditional>
              </Text>

              <Text variant="h16" strong color="warning" className="mx-1">
                {convertAmountToAbbreviation(remainingLimit, true)}
              </Text>
              <Text variant="b12" className="w-400 font-normal">
                left out of {convertAmountToAbbreviation(todayLimit, true)}
              </Text>
            </Space>
            <Progress
              className={`cf-progress-bar orange-bar m-0 ${
                percent <= 0 ? 'empty-progress-bar' : ''
              } ${percent === 100 ? 'full-progress-bar' : ''}`}
              percent={percent}
              size="xs"
            />
            <Conditional if={!isFreeTrialActive}>
              <Text
                variant="b12"
                className="w-400 font-normal mt-1 text-right"
                color="bodyLight"
              >
                Renewing in{' '}
                {`${hours ? `${hours} hr` : ''} ${mins ? `${mins} mins` : ''}`}
              </Text>
            </Conditional>
          </div>
        </Conditional>
      </Space>
      <Space alignItems="end" justifyContent="space-between">
        <div className={onDemandLimitLoader ? 'mt-1' : ''}>
          {disableSettleNow && disabledInfoText ? (
            <Popup
              content={
                <Text variant="b12" color="bodyLight">
                  {disabledInfoText}
                </Text>
              }
              trigger={<span>{withdrawNowButton}</span>}
              position="bottom center"
            />
          ) : (
            withdrawNowButton
          )}
        </div>
        <Space alignItems="center" gap={1.5}>
          <Text variant="b12" color="bodyLight">
            Last updated at{' '}
            {eligibleFundsData.lastUpdatedAt || PLACEHOLDER_TEXT}
          </Text>
          <Text
            variant="b12"
            color="primary"
            className="pointer refresh-cta"
            onClick={refreshCardData(ON_DEMAND_LIMIT)}
            data-event-name="Icon_Refresh"
          >
            <CustomIcon componentName="REFRESH" />
            Refresh
          </Text>
        </Space>
      </Space>
    </div>
  );
};

OnDemandSummaryCard.propTypes = {
  isFreeTrialActive: PropTypes.bool.isRequired,
  freeTrialObj: PropTypes.object,
  eligibleFundsData: PropTypes.object,
  refreshCardData: PropTypes.func.isRequired,
  onDemandDailyLimit: PropTypes.any,
  remainingSettlementsLimit: PropTypes.any,
  onDemandLimitLoader: PropTypes.bool,
  onWithdrawNow: PropTypes.func.isRequired,
  disableSettleNow: PropTypes.bool,
  disabledInfoText: PropTypes.string,
};

export default OnDemandSummaryCard;
